import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { orderByProperty, clearClassFromElements, getTextWidth } from '../utils';

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss']
})
export class MultiSelectComponent implements OnInit {
  searchTerm: string;
  showMenu: boolean;
  autoCompleteOptionIndex: number;
  clickedInside: boolean;
  allSelected: boolean;

  @Input()
  options: any[];

  @Input()
  selectedOptions: any[];

  @Input()
  key: string;

  @Input()
  allByDefault: boolean;

  @Input()
  multiSelectLabel: string;

  @Input()
  darkMode: boolean;

  @Output()
  optionsChanged = new EventEmitter<any[]>();

  constructor() {}

  ngOnInit() {
    this.showMenu = false;
    this.clickedInside = false;
    this.searchTerm = '';
    this.autoCompleteOptionIndex = -1;
    if (this.selectedOptions == null) {
      this.selectedOptions = [];
    } else {
      this.selectedOptions.forEach(option => {
        this.options = this.options.filter((it: any) => {
          return option.id !== it.id;
        });
      });
    }

    this.orderOptions();
    if (this.allByDefault) {
      this.selectAll();
    }
  }

  selectAll() {
    this.options = this.options.filter(option => {
      this.selectedOptions.push(option);
      return false;
    });
    this.allSelected = true;
    this.optionsChanged.emit(this.selectedOptions);
  }

  unselectAll() {
    this.selectedOptions = this.selectedOptions.filter(option => {
      this.options.push(option);
      return false;
    });
    this.orderOptions();
    this.allSelected = false;
    this.optionsChanged.emit(this.selectedOptions);
  }

  unselectOption(option: any) {
    this.clickedInside = true;
    this.selectedOptions = this.selectedOptions.filter((it: any) => {
      return option !== it;
    });

    this.options.push(option);
    this.orderOptions();
    this.optionsChanged.emit(this.selectedOptions);
    this.autoCompleteOptionIndex = -1;
  }

  selectOption(option: any) {
    this.clickedInside = true;
    this.options = this.options.filter((it: any) => {
      return option !== it;
    });

    this.selectedOptions.push(option);
    this.optionsChanged.emit(this.selectedOptions);
    this.searchTerm = '';
  }

  /**
   * @name: adjustWidth
   * @description: triggered when a key is pressed while focused on the multi-select input. This function
   *               adjusts the width of the input element in accordance with the length of the text in the input.
   *               Additionally, this handles deleting a pill if backspace is pressed while the input element
   *               does not contain a search term.
   * @param {event} e
   */
  adjustWidth(e) {
    const input = e.target;

    if (e.keyCode === 8 && input.value === '') {
      if (this.selectedOptions.length > 0) {
        const option = this.selectedOptions.pop();
        this.unselectOption(option);
      }
    } else {
      // This is triggered on a keydown so the new character doesn't exist
      // in the value. Manually adding an "M" because it is a wide character so this
      // helps make sure we account for whatever the actual character being entered is.
      const stringLength = getTextWidth(input.value + 'M', '16px arial');
      input.style.width = stringLength + 10 + 'px';
    }
  }

  /**
   * @name: focusOnInput
   * @description: ensures that the input element is always in focus when the drop-down is clicked on
   *               as well as increases the size of the input field.
   */
  focusOnInput(ev: any) {
    for (let i = 0; i < ev.target.children.length; i++) {
      if (ev.target.children[i].id === 'm-s__input') {
        ev.target.children[i].focus();
        ev.target.children[i].style.minWidth = '2.2rem';
        this.openMenu();
        break;
      }
    }
  }

  /**
   * @name: resetWidth
   * @description: called when the drop-down menu closes. This is used to reduce the size of the search
   *               field when the drop-down is not in use.
   */
  resetWidth() {
    this.searchTerm = '';

    const multiSelects = [].slice.call(document.getElementsByClassName('multi-select'));

    for (let i = 0; i < multiSelects.length; i++) {
      for (let a = 0; a < multiSelects[i].children.length; a++) {
        if (multiSelects[i].children[a].id === 'm-s__input') {
          multiSelects[i].children[a].style.minWidth = '0';
          multiSelects[i].children[a].style.width = '1px';
          break;
        }
      }
    }
  }

  /**
   * @name: navigateAutoCompleteOptions
   * @description: This function contains the controls for navigating through the auto-complete options of the drop-down. The up and down
   *               arrows on the keyboard allow the user to hightlight the value they want to select. Hitting the 'Enter' key
   *               will trigger add the highlighted option to the list of selected options.
   * @param {event} e
   */
  navigateAutoCompleteOptions(e) {
    const availableOptions = this.options.length > 0 ? this.options.length : null;
    const selectedOptions = this.selectedOptions.length > 0 ? this.selectedOptions.length : null;

    let numOfItems;
    if (availableOptions && selectedOptions) {
      numOfItems = availableOptions + 1;
    } else if (availableOptions && !selectedOptions) {
      numOfItems = availableOptions;
    } else if (!availableOptions && selectedOptions) {
      numOfItems = 0;
    } else {
      numOfItems = 1;
    }

    if (e.keyCode === 40) {
      // Down Arrow
      if (availableOptions || selectedOptions) {
        if (!this.showMenu) {
          this.showMenu = true;
        } else {
          if (this.autoCompleteOptionIndex < numOfItems) {
            const container = e.target.parentElement.getElementsByClassName('multi-select__menu-scrollbar')[0];
            clearClassFromElements(container.children, 'selected');
            this.autoCompleteOptionIndex++;
            container.children[this.autoCompleteOptionIndex].classList.add('selected');
          }
        }
      }
    } else if (e.keyCode === 38) {
      // Up Arrow
      if (this.options.length > 0 || numOfItems === 0) {
        const container = e.target.parentElement.getElementsByClassName('multi-select__menu-scrollbar')[0];
        clearClassFromElements(container.children, 'selected');
        this.autoCompleteOptionIndex--;
        if (this.autoCompleteOptionIndex >= 0) {
          container.children[this.autoCompleteOptionIndex].classList.add('selected');
        } else {
          this.autoCompleteOptionIndex = -1;
        }
      }
    } else if (e.keyCode === 13) {
      // Enter key
      if (this.autoCompleteOptionIndex > -1) {
        const container = e.target.parentElement.getElementsByClassName('multi-select__menu-scrollbar')[0];
        clearClassFromElements(container.children, 'selected');
        const selectedNode = container.children[this.autoCompleteOptionIndex];
        selectedNode.click();
        this.autoCompleteOptionIndex = -1;
        this.searchTerm = '';
      } else {
        if (!this.searchTerm || this.searchTerm.length === 0) {
          this.closeMenu();
        }
      }
    } else if (e.keyCode === 9) {
      // Tab key
      this.closeMenu();
    }
  }

  clickOutside() {
    if (this.clickedInside === false) {
      this.closeMenu();
    }
    this.clickedInside = false;
  }

  /**
   * @name: openMenu
   * @description: used to change the showMenu variable to true;
   */
  openMenu() {
    this.showMenu = true;
  }

  closeMenu() {
    this.showMenu = false;
    this.autoCompleteOptionIndex = -1;
  }

  orderOptions() {
    this.options = orderByProperty(this.options, this.key);
  }

  setSearchTerm(newSearchTerm: string) {
    this.searchTerm = newSearchTerm;
  }
}
